// src/components/BoardPack/pages/new_chart_component.jsx
import React, { useState, useEffect } from "react";
import { apiCall } from "@/api/apiCall";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import NewChartComponent from "../components/charts/NewChartComponent";
import { useQueue } from "@/context/QueueContext";
import { useNavigate } from "react-router-dom";

const NewChartComponentPage = ({
  year     = "",
  month    = "",
  contract = "",
  page     = null,
  isRefresh = false
}) => {
  const { enqueue } = useQueue();
  const navigate = useNavigate();

  const [chartData, setChartData] = useState(null);
  const [lineYear,  setLineYear]  = useState("");
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  // ── Transform ────────────────────────────────────────────────────────────
  // Response:
  // {
  //   meta: { year: 2025, month: "Oct", ... },
  //   data: {
  //     "0020013 - TAIF ON STREET": {
  //       "Ticket_Count": { "2023": { Jan: "86,471", ... }, "2024": {...}, "2025": {...} }
  //     }
  //   }
  // }
  const transformApiData = (apiData) => {
    const meta       = apiData?.meta ?? {};
    const dataObj    = apiData?.data ?? {};

    const contractNames = Object.keys(dataObj);
    if (!contractNames.length) return null;

    const contractData = dataObj[contractNames[0]];

    // First key inside contractData = KPI key (e.g. "Ticket_Count")
    const kpiKeys = Object.keys(contractData);
    if (!kpiKeys.length) return null;
    const kpiObj = contractData[kpiKeys[0]];

    const parseNum = (val) => {
  if (val == null || val === "-") return 0;
  const parsed = parseFloat(String(val).replace(/[,%]/g, ""));
  return isNaN(parsed) ? 0 : parsed;
};

    const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

    const years         = Object.keys(kpiObj).filter((k) => /^\d{4}$/.test(k)).sort();
    if (!years.length) return null;

    const selectedYear  = String(meta.year  ?? "");
    const selectedMonth = String(meta.month ?? "");
    const latestYr      = selectedYear || String(Math.max(...years.map(Number)));

    const result = {};
    years.forEach((yr) => {
      result[yr] = {};
      MONTHS.forEach((mon) => {
        // Current year: only include months up to selected month
        if (yr === selectedYear && selectedMonth) {
          const selIdx = MONTHS.indexOf(selectedMonth);
          const curIdx = MONTHS.indexOf(mon);
          if (selIdx !== -1 && curIdx > selIdx) return;
        }
        result[yr][mon] = parseNum(kpiObj?.[yr]?.[mon]);
      });
    });

    return { chartData: result, lineYear: latestYr };
  };

  // ── Fetch — re-runs on year / month / contract change ───────────────────
  useEffect(() => {
    if (!year || !month || !contract) return;
    fetchData();
  }, [year, month, contract]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Resolve {{year}} / {{month}} placeholders
      const rawParams = page?.params ?? {};
      const resolvedParams = Object.fromEntries(
        Object.entries(rawParams).map(([k, v]) => {
          if (typeof v === "string") {
            v = v.replace("{{year}}", year).replace("{{month}}", month);
          }
          return [k, v];
        })
      );

      // Payload: contract, year, month, kpi (empty), version from page.params
      const params = {
        contract:page?.params?.contract,
        year,
        month,
        version: "",
        kpi:     page?.params?.version,        // kpi always stays empty
      };

      const endpoint = page?.api ?? "/board-pack/page-twelve";
      const response = await enqueue(() => apiCall("GET", endpoint, params,navigate,false,false,isRefresh));

      if (response?.success) {
        const transformed = transformApiData(response);
        if (transformed) {
          setChartData(transformed.chartData);
          setLineYear(transformed.lineYear);
        }
      } else {
        setError(response?.message || "Failed to load data. Please try again later.");
      }
    } catch (err) {
      setError(err?.message || "Failed to load data. Please try again later.");
      console.error("Error fetching chart data:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingComponent />;
  if (error)   return <ErrorState error={error} onRetry={fetchData} />;
  if (!chartData) return <ErrorState error="No data available." onRetry={fetchData} />;

  return (
    <NewChartComponent
      title={page?.title}
      subtitle={page?.subtitle}
      chartTitle={page?.in_chart_content_line_1}
      yAxisLabel={page?.yAxisLabel  ?? "TRANSACTIONS"}
      bottomLabel={page?.bottomLabel ?? "MONTHS"}
      chartData={chartData}
      lineYear={lineYear}
      colors={page?.colors}
    />
  );
};

export default NewChartComponentPage;